/**
 * Syllabus Hooks
 * React Query hooks for syllabus upload and management
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/lib/auth';
import type {
  CourseSyllabus,
  SyllabusStatus,
  ParsedSyllabusData,
  CreateSyllabusInput,
  SyllabusFileType,
} from '@/types/syllabus';

// ============================================================================
// DATABASE ROW TYPE
// ============================================================================

interface CourseSyllabusRow {
  id: string;
  user_id: string;
  course_id: string | null;
  file_name: string;
  file_url: string;
  file_type: string | null;
  file_size_bytes: number | null;
  status: string;
  error_message: string | null;
  parsed_data: unknown | null;
  extraction_confidence: number | null;
  course_name_extracted: string | null;
  professor_name_extracted: string | null;
  semester_extracted: string | null;
  processing_started_at: string | null;
  processing_completed_at: string | null;
  processing_metadata: unknown | null;
  created_at: string | null;
  updated_at: string | null;
}

// Helper to convert database row to CourseSyllabus
function toSyllabus(row: CourseSyllabusRow): CourseSyllabus {
  return {
    id: row.id,
    user_id: row.user_id,
    course_id: row.course_id,
    file_name: row.file_name,
    file_url: row.file_url,
    file_type: row.file_type as SyllabusFileType | null,
    file_size_bytes: row.file_size_bytes,
    status: row.status as SyllabusStatus,
    error_message: row.error_message,
    parsed_data: row.parsed_data as ParsedSyllabusData | null,
    extraction_confidence: row.extraction_confidence ?? 0,
    course_name_extracted: row.course_name_extracted,
    professor_name_extracted: row.professor_name_extracted,
    semester_extracted: row.semester_extracted,
    processing_started_at: row.processing_started_at,
    processing_completed_at: row.processing_completed_at,
    processing_metadata: row.processing_metadata as CourseSyllabus['processing_metadata'],
    created_at: row.created_at,
    updated_at: row.updated_at,
  };
}

// ============================================================================
// QUERY KEYS
// ============================================================================

export const syllabusKeys = {
  all: ['syllabi'] as const,
  list: (userId: string) => [...syllabusKeys.all, 'list', userId] as const,
  byCourse: (userId: string, courseId: string) =>
    [...syllabusKeys.all, 'course', userId, courseId] as const,
  detail: (syllabusId: string) => [...syllabusKeys.all, 'detail', syllabusId] as const,
};

// ============================================================================
// QUERY HOOKS
// ============================================================================

/**
 * Fetch all syllabi for the current user
 */
export function useSyllabi() {
  const { user } = useAuth();

  return useQuery({
    queryKey: syllabusKeys.list(user?.id ?? ''),
    queryFn: async (): Promise<CourseSyllabus[]> => {
      if (!user?.id) return [];

      const { data, error } = await (supabase
        .from('course_syllabi' as any)
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false }) as unknown as Promise<{
        data: CourseSyllabusRow[] | null;
        error: any;
      }>);

      if (error) throw error;
      return (data ?? []).map(toSyllabus);
    },
    enabled: !!user?.id,
    staleTime: 60 * 1000, // 1 minute
  });
}

/**
 * Fetch syllabi for a specific course
 */
export function useSyllabusByCourse(courseId: string | undefined) {
  const { user } = useAuth();

  return useQuery({
    queryKey: syllabusKeys.byCourse(user?.id ?? '', courseId ?? ''),
    queryFn: async (): Promise<CourseSyllabus[]> => {
      if (!user?.id || !courseId) return [];

      const { data, error } = await (supabase
        .from('course_syllabi' as any)
        .select('*')
        .eq('user_id', user.id)
        .eq('course_id', courseId)
        .order('created_at', { ascending: false }) as unknown as Promise<{
        data: CourseSyllabusRow[] | null;
        error: any;
      }>);

      if (error) throw error;
      return (data ?? []).map(toSyllabus);
    },
    enabled: !!user?.id && !!courseId,
  });
}

/**
 * Fetch a single syllabus by ID
 * Security: Filters by both syllabusId AND user_id to prevent unauthorized access
 */
export function useSyllabus(syllabusId: string | null) {
  const { user } = useAuth();

  return useQuery({
    queryKey: syllabusKeys.detail(syllabusId ?? ''),
    queryFn: async (): Promise<CourseSyllabus | null> => {
      if (!syllabusId || !user?.id) return null;

      const { data, error } = await (supabase
        .from('course_syllabi' as any)
        .select('*')
        .eq('id', syllabusId)
        .eq('user_id', user.id) // Security: Ensure user can only access their own syllabi
        .maybeSingle() as unknown as Promise<{
        data: CourseSyllabusRow | null;
        error: any;
      }>);

      if (error) throw error;
      return data ? toSyllabus(data) : null;
    },
    enabled: !!syllabusId && !!user?.id,
    refetchInterval: (data) => {
      // Poll while processing
      if (data?.state?.data?.status === 'processing') {
        return 3000; // 3 seconds
      }
      return false;
    },
  });
}

// ============================================================================
// MUTATION HOOKS
// ============================================================================

/**
 * Create a new syllabus record (before upload)
 */
export function useCreateSyllabus() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (input: CreateSyllabusInput): Promise<CourseSyllabus> => {
      if (!user?.id) throw new Error('Not authenticated');

      const { data, error } = await (supabase
        .from('course_syllabi' as any)
        .insert({
          user_id: user.id,
          course_id: input.course_id || null,
          file_name: input.file_name,
          file_url: input.file_url,
          file_type: input.file_type || null,
          file_size_bytes: input.file_size_bytes || null,
          status: 'pending',
        })
        .select()
        .single() as unknown as Promise<{ data: CourseSyllabusRow; error: any }>);

      if (error) throw error;
      return toSyllabus(data);
    },
    onSuccess: () => {
      if (user?.id) {
        queryClient.invalidateQueries({ queryKey: syllabusKeys.list(user.id) });
      }
    },
  });
}

/**
 * Update syllabus status
 */
export function useUpdateSyllabusStatus() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({
      syllabusId,
      status,
      errorMessage,
      parsedData,
      extractionConfidence,
    }: {
      syllabusId: string;
      status: SyllabusStatus;
      errorMessage?: string;
      parsedData?: ParsedSyllabusData;
      extractionConfidence?: number;
    }): Promise<CourseSyllabus> => {
      if (!user?.id) throw new Error('Not authenticated');

      const updateData: Record<string, unknown> = {
        status,
        updated_at: new Date().toISOString(),
      };

      if (status === 'processing') {
        updateData.processing_started_at = new Date().toISOString();
      }

      if (status === 'completed' || status === 'failed') {
        updateData.processing_completed_at = new Date().toISOString();
      }

      if (errorMessage !== undefined) {
        updateData.error_message = errorMessage;
      }

      if (parsedData !== undefined) {
        updateData.parsed_data = parsedData;
        updateData.course_name_extracted = parsedData.course_name || null;
        updateData.professor_name_extracted = parsedData.professor_name || null;
        updateData.semester_extracted = parsedData.semester || null;
      }

      if (extractionConfidence !== undefined) {
        updateData.extraction_confidence = extractionConfidence;
      }

      // SECURITY: Always include user_id check to prevent updating other users' syllabi
      const { data, error } = await (supabase
        .from('course_syllabi' as any)
        .update(updateData)
        .eq('id', syllabusId)
        .eq('user_id', user.id)
        .select()
        .single() as unknown as Promise<{ data: CourseSyllabusRow; error: any }>);

      if (error) throw error;
      return toSyllabus(data);
    },
    onSuccess: (data) => {
      if (user?.id) {
        queryClient.invalidateQueries({ queryKey: syllabusKeys.list(user.id) });
        queryClient.invalidateQueries({ queryKey: syllabusKeys.detail(data.id) });
        if (data.course_id) {
          queryClient.invalidateQueries({
            queryKey: syllabusKeys.byCourse(user.id, data.course_id),
          });
        }
      }
    },
  });
}

/**
 * Link syllabus to a course
 */
export function useLinkSyllabusToCourse() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({
      syllabusId,
      courseId,
    }: {
      syllabusId: string;
      courseId: string;
    }): Promise<CourseSyllabus> => {
      if (!user?.id) throw new Error('Not authenticated');

      // SECURITY: Always include user_id check to prevent linking other users' syllabi
      const { data, error } = await (supabase
        .from('course_syllabi' as any)
        .update({ course_id: courseId, updated_at: new Date().toISOString() })
        .eq('id', syllabusId)
        .eq('user_id', user.id)
        .select()
        .single() as unknown as Promise<{ data: CourseSyllabusRow; error: any }>);

      if (error) throw error;
      return toSyllabus(data);
    },
    onSuccess: (data) => {
      if (user?.id) {
        queryClient.invalidateQueries({ queryKey: syllabusKeys.list(user.id) });
        queryClient.invalidateQueries({ queryKey: syllabusKeys.detail(data.id) });
      }
    },
  });
}

/**
 * Delete a syllabus
 */
export function useDeleteSyllabus() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (syllabusId: string): Promise<void> => {
      if (!user?.id) throw new Error('Not authenticated');

      // SECURITY: Always include user_id check to prevent deleting other users' syllabi
      const { error } = await (supabase
        .from('course_syllabi' as any)
        .delete()
        .eq('id', syllabusId)
        .eq('user_id', user.id) as unknown as Promise<{ error: any }>);

      if (error) throw error;
    },
    onSuccess: () => {
      if (user?.id) {
        queryClient.invalidateQueries({ queryKey: syllabusKeys.list(user.id) });
      }
    },
  });
}

// ============================================================================
// UTILITY HOOKS
// ============================================================================

/**
 * Get count of syllabi by status
 */
export function useSyllabusCounts() {
  const { data: syllabi = [], isLoading } = useSyllabi();

  return {
    total: syllabi.length,
    pending: syllabi.filter((s) => s.status === 'pending').length,
    processing: syllabi.filter((s) => s.status === 'processing').length,
    completed: syllabi.filter((s) => s.status === 'completed').length,
    failed: syllabi.filter((s) => s.status === 'failed').length,
    isLoading,
  };
}

/**
 * Check if user has any syllabi for a course
 */
export function useHasSyllabusForCourse(courseId: string | undefined): boolean {
  const { data: syllabi = [] } = useSyllabusByCourse(courseId);
  return syllabi.some((s) => s.status === 'completed');
}

/**
 * Update syllabus parsed data (for editing exams, assignments, etc.)
 */
export function useUpdateSyllabusParsedData() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({
      syllabusId,
      parsedData,
    }: {
      syllabusId: string;
      parsedData: ParsedSyllabusData;
    }): Promise<CourseSyllabus> => {
      if (!user?.id) throw new Error('Not authenticated');

      const updateData: Record<string, unknown> = {
        parsed_data: parsedData,
        updated_at: new Date().toISOString(),
        // Update extracted fields too
        course_name_extracted: parsedData.course_name || null,
        professor_name_extracted: parsedData.professor_name || null,
        semester_extracted: parsedData.semester || null,
      };

      // SECURITY: Always include user_id check
      const { data, error } = await (supabase
        .from('course_syllabi' as any)
        .update(updateData)
        .eq('id', syllabusId)
        .eq('user_id', user.id)
        .select()
        .single() as unknown as Promise<{ data: CourseSyllabusRow; error: any }>);

      if (error) throw error;
      return toSyllabus(data);
    },
    onSuccess: (data) => {
      if (user?.id) {
        queryClient.invalidateQueries({ queryKey: syllabusKeys.list(user.id) });
        queryClient.invalidateQueries({ queryKey: syllabusKeys.detail(data.id) });
        if (data.course_id) {
          queryClient.invalidateQueries({
            queryKey: syllabusKeys.byCourse(user.id, data.course_id),
          });
        }
      }
    },
  });
}
